import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web';
import { PersistGate } from 'redux-persist/integration/react';
import axios from 'axios';
import { ApolloProvider } from '@apollo/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import configureStore from './store/store';
import { navigateRef } from './utils/navigateRef';
import { addIconLibrary } from './actions/icons';
import { resolveClient } from './graphql/resolver';

import './App.scss';

import keycloak, { auth } from './config';
import { getMyNetwork } from './actions/networks';
import { getBentoItems } from './actions/auth';
import jwt_decode from 'jwt-decode';

const keycloakProviderInitConfig = {
  // onLoad: 'check-sso',
  onLoad: 'login-required',
  checkLoginIframe: false,
  redirectUri: window.location.origin,
  pkceMethod: 'S256',
};

export const { persistor, store  } = configureStore();

const loading = () => <div className="animated fadeIn pt-3 text-center d-none"><i className="fas d-none fa-redo fa-spin" /></div>;

const AdminRoute = React.lazy(() => import('./containers/AdminLayout/AdminLayout'));
const Login = React.lazy(() => import('./views/Pages/Login/Login'));
const Logout = React.lazy(() => import('./views/Pages/Logout/Logout'));
const EndUserTerms = React.lazy(() => import('./views/Pages/EndUserTerms/EndUserTerms'));
const Register = React.lazy(() => import('./views/Pages/Register/Register'));
const Page404 = React.lazy(() => import('./views/Pages/Page404/Page404'));
const Page401 = React.lazy(() => import('./views/Pages/Page401/Page401'));
const Page500 = React.lazy(() => import('./views/Pages/Page500/Page500'));
const Preloader = React.lazy(() => import('./views/Home/Preloader'));
const queryClient = new QueryClient();

addIconLibrary();

axios.interceptors.request.use(function (config) {
  try {
    if(!config.url.includes(auth.auth_url)){
      let currentNetwork = localStorage.getItem('currentNetwork');
      if(currentNetwork != null && currentNetwork != '' && currentNetwork != 'undefined'){
        config.headers.common['X-NETWORK-ID'] = currentNetwork;
      }
    }
  } catch (error) {}

  return config;
});


function NavigateRefSync() {
  const navigate = useNavigate();
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  return null;
}

function AppRouter({ client }){
  const { initialized } = useKeycloak();
  const [isNetworkSet, setIsNetworkSet ] = useState(false)
  const navigate = useNavigate();

  useEffect(()=>{
    if(initialized){
      setNetwork()
    }

  },[initialized])

  const setNetwork = async () => {
    let currentNetwork = localStorage.getItem('currentNetwork');
    let recentNetworks = localStorage.getItem('recentNetworks');

    const urlParams = new URLSearchParams(window.location.search);
    let currentNetworkDetails = {}

    try {
      const networkParam = urlParams.get('networkParam');
      if(networkParam != null && networkParam != ""){
        currentNetwork = networkParam
        localStorage.setItem('currentNetwork', networkParam)
      }

       //setup recent network
      try {
        const accessToken = localStorage.getItem('access_token');
        let auth = null
        if (accessToken !== undefined) {
          try {
            auth = jwt_decode(accessToken);
          }
          catch (e) {}
        }
        let recentNetworksParsed = JSON.parse(recentNetworks)
        if(recentNetworksParsed?.user == undefined ||  recentNetworksParsed?.recentNetworks == undefined){
          localStorage.setItem('recentNetworks', JSON.stringify({user:auth.email, recentNetworks:[]}));
        } else {
          if(recentNetworksParsed?.user != auth.email){
            localStorage.setItem('recentNetworks', JSON.stringify({user:auth.email, recentNetworks:[]}));
          }
        }
      } catch (error) {
        localStorage.setItem('recentNetworks', JSON.stringify({user:auth.email, recentNetworks:[]}));
      }

      const getMyNetworkResp = await getMyNetwork();
      let doesExist = false;
      getMyNetworkResp.data.map((network)=>{
        if(currentNetwork == network.name){
          doesExist = true;
          currentNetworkDetails = network;
        }
      });

      if(!doesExist){
        try {
          localStorage.setItem('currentNetwork', getMyNetworkResp.data[0].name)
          currentNetworkDetails = getMyNetworkResp.data[0];
        } catch (error) {}
      }

      // check if has coatro app
      let hasCoatro = false;
      let bentoItems = getBentoItems(currentNetworkDetails)
      bentoItems.map((item)=>{
        if(item.name == "Coatro"){
          hasCoatro = true
        }
      })

      if(!hasCoatro){
        setIsNetworkSet(true)
        return navigate('/401', { replace: true });
      }
      setIsNetworkSet(true)
    } catch (error) {
      setIsNetworkSet(true)
    }

  };

  if (!initialized || !isNetworkSet) {
    return <div />;
  }


  return (
    <ApolloProvider client={client}>
      <QueryClientProvider client={queryClient}>
        <NavigateRefSync />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/enduserterms" element={<EndUserTerms />} />
          <Route path="/register" element={<Register />} />
          <Route path="/404" element={<Page404 />} />
          <Route path="/401" element={<Page401 />} />
          <Route path="/500" element={<Page500 />} />
          <Route path="/home" element={<Preloader />} />
          <Route path="/*" element={<AdminRoute />} />
        </Routes>
      </QueryClientProvider>
    </ApolloProvider>
  );
}

function App(){
  const [client, setClient] = useState(null);

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={keycloakProviderInitConfig}
      onEvent={(event, error) => {
        switch(event) {
          case 'onAuthSuccess':
            break;
          case 'onAuthLogout':
            break;
          case 'onTokenExpired':
            keycloak.updateToken(5).then(() => {
              // token refreshed
            }).catch(() => {
              navigateRef.current?.('/login');
              keycloak.logout();
            });
            break;
          case 'onInitError':
            break;
          case 'onAuthError':
            setClient(true);
            break;
          default:
            break;
        }
      }}
      onTokens={(tokens) => {
        localStorage.setItem('access_token', tokens.token);
        axios.defaults.headers.common.Authorization = `Bearer ${tokens.token}`;
        setClient(resolveClient(tokens.token, keycloak));
      }}
    >
      <BrowserRouter>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            {client && <AppRouter client={client} />}
          </PersistGate>
        </Provider>
      </BrowserRouter>
    </ReactKeycloakProvider>
  );
}

export default App;

